import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  ShieldCheck, FileText, Inbox, Key, Plus, Trash2, Edit3, Eye, Send, Sparkles, Check, Copy,
} from 'lucide-react';
import { logger } from './utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'reviewer', label: 'Review Queue', icon: Inbox },
  { id: 'knowledge', label: 'Knowledge Base', icon: FileText },
  { id: 'tokens', label: 'Access Tokens', icon: Key },
  { id: 'internal-ask', label: 'Internal Ask', icon: Sparkles },
];

const STATUS_BADGE = {
  ai_pending_review: { label: 'Pending review', color: 'bg-amber-100 text-amber-800' },
  ai_only: { label: 'AI only', color: 'bg-blue-100 text-blue-800' },
  ai_human_verified: { label: 'AI + human verified', color: 'bg-emerald-100 text-emerald-800' },
  human_only: { label: 'Human only', color: 'bg-emerald-100 text-emerald-800' },
  escalated_no_answer: { label: 'Escalated', color: 'bg-rose-100 text-rose-700' },
};

export default function ComplianceAdminPortal() {
  const [activeTab, setActiveTab] = useState('reviewer');
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold flex items-center gap-2" data-testid="compliance-admin-title">
            <ShieldCheck className="w-5 h-5 text-emerald-600" /> AI Compliance Response Platform
          </h1>
          <a href="/admin/dashboard" className="text-xs text-blue-600 hover:underline">← Back to Admin</a>
        </div>
        <nav className="container mx-auto px-4 pb-1 flex gap-1 flex-wrap">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                data-testid={`compliance-tab-${t.id}`}
                className={`px-3 py-2 text-sm rounded-t-md inline-flex items-center gap-1.5 border-b-2 transition ${
                  activeTab === t.id ? 'border-emerald-600 text-emerald-700 font-medium' : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Icon className="w-4 h-4" />{t.label}
              </button>
            );
          })}
        </nav>
      </header>

      <main className="container mx-auto px-4 py-6">
        {activeTab === 'reviewer' && <ReviewQueue />}
        {activeTab === 'knowledge' && <KnowledgeBase />}
        {activeTab === 'tokens' && <AccessTokens />}
        {activeTab === 'internal-ask' && <InternalAsk />}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// REVIEW QUEUE
// ---------------------------------------------------------------------------
function ReviewQueue() {
  const [filter, setFilter] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter ? `?status=${filter}` : '';
      const { data } = await axios.get(`${API}/compliance/questions${q}`);
      setItems(data || []);
    } catch (err) { logger.error('queue load', err); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="reviewer-queue">
      <div className="flex items-center gap-2 mb-4">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="queue-status-filter"
          className="px-3 py-2 border rounded text-sm"
        >
          <option value="">All statuses</option>
          <option value="ai_pending_review">Pending review</option>
          <option value="escalated_no_answer">Escalated (no AI answer)</option>
          <option value="ai_human_verified">Human verified</option>
          <option value="ai_only">AI only</option>
          <option value="human_only">Human only</option>
        </select>
        <button onClick={load} className="text-xs text-blue-600 hover:underline">Refresh</button>
      </div>

      {loading ? <p className="text-sm text-slate-500">Loading…</p> :
        items.length === 0 ? <p className="text-sm text-slate-500 py-12 text-center">No questions in queue.</p> :
        <ul className="space-y-3">
          {items.map((q) => {
            const sb = STATUS_BADGE[q.status] || STATUS_BADGE.ai_pending_review;
            return (
              <li key={q.id} data-testid={`queue-item-${q.id}`} className="bg-white border rounded-lg p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-500 mb-1">{q.asker_label || 'Internal'} · {q.created_at?.slice(0, 16).replace('T', ' ')}</div>
                    <p className="font-medium text-sm mb-1">{q.question_text}</p>
                    <p className="text-xs text-slate-600 line-clamp-2">{q.final_answer || q.ai_answer}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className={`px-2 py-0.5 rounded text-xs ${sb.color}`} data-testid={`queue-status-${q.id}`}>{sb.label}</span>
                    <p className="text-xs text-slate-400 mt-1">conf: {q.ai_confidence}</p>
                  </div>
                </div>
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => setOpenId(openId === q.id ? null : q.id)}
                    data-testid={`queue-open-${q.id}`}
                    className="text-xs text-blue-600 hover:underline"
                  >{openId === q.id ? 'Close' : 'Open & review'}</button>
                </div>
                {openId === q.id && <ReviewerEdit question={q} onSaved={() => { setOpenId(null); load(); }} />}
              </li>
            );
          })}
        </ul>
      }
    </div>
  );
}

function ReviewerEdit({ question, onSaved }) {
  const [answer, setAnswer] = useState(question.final_answer || question.ai_answer || '');
  const [status, setStatus] = useState('ai_human_verified');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/compliance/questions/${question.id}/review`, {
        final_answer: answer, status, reviewer_notes: notes,
      });
      onSaved();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="mt-3 p-3 bg-slate-50 rounded space-y-2 border" data-testid={`reviewer-edit-${question.id}`}>
      {question.ai_sources?.length > 0 && (
        <div className="text-xs text-slate-500">
          <span className="font-medium">Sources AI used:</span>{' '}
          {question.ai_sources.map((s) => `${s.title} (${s.visibility})`).join(' · ')}
        </div>
      )}
      <label className="block">
        <span className="block text-xs font-medium mb-1">Final answer</span>
        <textarea
          required
          rows={5}
          value={answer}
          onChange={(e) => setAnswer(e.target.value.slice(0, 5000))}
          data-testid={`reviewer-answer-${question.id}`}
          className="w-full px-2 py-1.5 border rounded text-sm"
        />
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label>
          <span className="block text-xs font-medium mb-1">Resulting status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid={`reviewer-status-${question.id}`} className="w-full px-2 py-1.5 border rounded text-sm">
            <option value="ai_human_verified">AI + human verified</option>
            <option value="human_only">Human-only answer (overrode AI)</option>
            <option value="ai_only">AI only (auto-approve as-is)</option>
          </select>
        </label>
        <label>
          <span className="block text-xs font-medium mb-1">Reviewer notes (internal)</span>
          <input value={notes} onChange={(e) => setNotes(e.target.value.slice(0, 1000))} className="w-full px-2 py-1.5 border rounded text-sm" />
        </label>
      </div>
      <button
        type="submit"
        disabled={saving}
        data-testid={`reviewer-save-${question.id}`}
        className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1"
      >
        <Check className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Approve & save'}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// KNOWLEDGE BASE
// ---------------------------------------------------------------------------
function KnowledgeBase() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setDocs((await axios.get(`${API}/compliance/knowledge`)).data || []); }
    catch (err) { logger.error('kb load', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm('Delete this knowledge base document?')) return;
    await axios.delete(`${API}/compliance/knowledge/${id}`);
    load();
  };

  return (
    <div data-testid="knowledge-base">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">Source documents the AI uses to answer compliance questions. Tag with `internal_only` to keep them out of any external response.</p>
        <button
          onClick={() => setShowNew(true)}
          data-testid="kb-add-doc-btn"
          className="px-3 py-2 bg-emerald-600 text-white rounded text-sm inline-flex items-center gap-1"
        >
          <Plus className="w-4 h-4" /> New document
        </button>
      </div>

      {showNew && <DocEditor onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {editing && <DocEditor docId={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      {loading ? <p className="text-sm text-slate-500">Loading…</p> :
        docs.length === 0 ? <p className="text-sm text-slate-500 py-12 text-center">No documents in knowledge base yet.</p> :
        <ul className="space-y-2">
          {docs.map((d) => (
            <li key={d.id} data-testid={`kb-doc-${d.id}`} className="bg-white border rounded p-3 flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">{d.title}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  <span className={`inline-block px-1.5 py-0.5 rounded mr-1.5 ${d.visibility === 'public' ? 'bg-emerald-100 text-emerald-700' : d.visibility === 'under_nda' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>
                    {d.visibility}
                  </span>
                  {d.source_type} · {d.content_length} chars · hash {d.content_hash}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => setEditing(d.id)} data-testid={`kb-edit-${d.id}`} className="text-slate-400 hover:text-blue-600"><Edit3 className="w-4 h-4" /></button>
                <button onClick={() => remove(d.id)} data-testid={`kb-delete-${d.id}`} className="text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </li>
          ))}
        </ul>
      }
    </div>
  );
}

function DocEditor({ docId, onClose, onSaved }) {
  const [form, setForm] = useState({ title: '', source_text: '', visibility: 'public', tags: '', source_type: 'policy' });
  const [loading, setLoading] = useState(!!docId);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!docId) return;
    axios.get(`${API}/compliance/knowledge/${docId}`).then(({ data }) => {
      setForm({
        title: data.title, source_text: data.source_text,
        visibility: data.visibility, source_type: data.source_type || 'policy',
        tags: (data.tags || []).join(', '),
      });
    }).finally(() => setLoading(false));
  }, [docId]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title, source_text: form.source_text,
      visibility: form.visibility, source_type: form.source_type,
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (docId) {
        await axios.put(`${API}/compliance/knowledge/${docId}`, payload);
      } else {
        await axios.post(`${API}/compliance/knowledge`, payload);
      }
      onSaved();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="kb-editor">
      <form onSubmit={submit} className="bg-white rounded-lg p-5 max-w-3xl w-full max-h-[90vh] overflow-y-auto space-y-3">
        <h3 className="font-semibold text-lg">{docId ? 'Edit' : 'New'} document</h3>
        <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" data-testid="kb-title" className="w-full px-3 py-2 border rounded" />
        <textarea required value={form.source_text} onChange={(e) => setForm({ ...form, source_text: e.target.value })} placeholder="Source text — the AI answers ONLY from this." rows={14} data-testid="kb-source-text" className="w-full px-3 py-2 border rounded font-mono text-xs" />
        <div className="grid grid-cols-3 gap-2">
          <select value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} data-testid="kb-visibility" className="px-3 py-2 border rounded text-sm">
            <option value="public">Public — quoted freely</option>
            <option value="under_nda">Under NDA — quoted only to NDA-signed askers</option>
            <option value="internal_only">Internal only — never quoted externally</option>
          </select>
          <input value={form.source_type} onChange={(e) => setForm({ ...form, source_type: e.target.value })} placeholder="source_type (policy/dpia/etc)" className="px-3 py-2 border rounded text-sm" />
          <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="tags (comma separated)" className="px-3 py-2 border rounded text-sm" />
        </div>
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600">Cancel</button>
          <button type="submit" disabled={saving} data-testid="kb-save" className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ACCESS TOKENS
// ---------------------------------------------------------------------------
function AccessTokens() {
  const [tokens, setTokens] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [issued, setIssued] = useState(null);
  const [form, setForm] = useState({ org_name: '', requester_name: '', requester_email: '', note: '' });

  const load = useCallback(async () => {
    try { setTokens((await axios.get(`${API}/compliance/access-tokens`)).data || []); }
    catch (err) { logger.error('tokens load', err); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const issue = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/compliance/access-tokens`, form);
      setIssued(data);
      setShowNew(false);
      setForm({ org_name: '', requester_name: '', requester_email: '', note: '' });
      load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed to issue');
    }
  };

  const revoke = async (id) => {
    if (!window.confirm('Revoke this access token? The recipient will lose access immediately.')) return;
    await axios.delete(`${API}/compliance/access-tokens/${id}`);
    load();
  };

  return (
    <div data-testid="access-tokens">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">Issue NDA-gated portal access links to prospect compliance teams. The full token is shown only once.</p>
        <button onClick={() => setShowNew(true)} data-testid="tokens-new-btn" className="px-3 py-2 bg-emerald-600 text-white rounded text-sm inline-flex items-center gap-1"><Plus className="w-4 h-4" /> New access token</button>
      </div>

      {issued && (
        <div className="bg-emerald-50 border border-emerald-300 rounded p-4 mb-4" data-testid="token-issued">
          <p className="font-medium text-emerald-900 mb-2">Access link issued — copy this now, it will not be shown again.</p>
          <div className="flex items-center gap-2 bg-white border rounded p-2 font-mono text-xs break-all">
            <span className="flex-1">{window.location.origin}{issued.share_path}</span>
            <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}${issued.share_path}`); }} className="text-blue-600"><Copy className="w-4 h-4" /></button>
          </div>
          <p className="text-xs text-emerald-700 mt-2">For: {issued.org_name} · {issued.requester_email}</p>
          <button onClick={() => setIssued(null)} className="mt-2 text-xs text-slate-500 hover:underline">Dismiss</button>
        </div>
      )}

      {showNew && (
        <form onSubmit={issue} className="bg-white border rounded p-4 mb-4 space-y-2" data-testid="token-form">
          <div className="grid grid-cols-2 gap-2">
            <input required value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} placeholder="Organisation (e.g. HSBC Compliance)" data-testid="token-org" className="px-3 py-2 border rounded text-sm" />
            <input required value={form.requester_name} onChange={(e) => setForm({ ...form, requester_name: e.target.value })} placeholder="Requester name" data-testid="token-name" className="px-3 py-2 border rounded text-sm" />
            <input required type="email" value={form.requester_email} onChange={(e) => setForm({ ...form, requester_email: e.target.value })} placeholder="Requester email" data-testid="token-email" className="px-3 py-2 border rounded text-sm" />
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note (optional)" className="px-3 py-2 border rounded text-sm" />
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setShowNew(false)} className="px-3 py-1.5 text-sm text-slate-600">Cancel</button>
            <button type="submit" data-testid="token-submit" className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded">Issue</button>
          </div>
        </form>
      )}

      <table className="w-full text-sm bg-white border rounded">
        <thead className="bg-slate-50 text-xs text-slate-600">
          <tr>
            <th className="text-left p-2">Org</th>
            <th className="text-left p-2">Requester</th>
            <th className="text-left p-2">Status</th>
            <th className="text-left p-2">Questions</th>
            <th className="text-left p-2">Last used</th>
            <th className="p-2"></th>
          </tr>
        </thead>
        <tbody>
          {tokens.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-6">No access tokens issued.</td></tr>}
          {tokens.map((t) => (
            <tr key={t.id} data-testid={`token-row-${t.id}`} className="border-t">
              <td className="p-2">{t.org_name}</td>
              <td className="p-2 text-xs">{t.requester_name}<br /><span className="text-slate-400">{t.requester_email}</span></td>
              <td className="p-2"><span className={`text-xs px-2 py-0.5 rounded ${t.status === 'agreed' ? 'bg-emerald-100 text-emerald-700' : t.status === 'revoked' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'}`}>{t.status}</span></td>
              <td className="p-2">{t.question_count || 0}</td>
              <td className="p-2 text-xs text-slate-500">{t.last_used_at?.slice(0, 16).replace('T', ' ') || '—'}</td>
              <td className="p-2">{t.status !== 'revoked' && <button onClick={() => revoke(t.id)} data-testid={`token-revoke-${t.id}`} className="text-rose-600 text-xs hover:underline">Revoke</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// INTERNAL ASK — owner/admin asks directly (sees internal_only sources)
// ---------------------------------------------------------------------------
function InternalAsk() {
  const [question, setQuestion] = useState('');
  const [thread, setThread] = useState([]);
  const [asking, setAsking] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (question.trim().length < 5) return;
    setAsking(true);
    try {
      const { data } = await axios.post(`${API}/compliance/ask-internal`, { question_text: question });
      setThread((t) => [{ ...data, question_text: question }, ...t]);
      setQuestion('');
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed');
    } finally { setAsking(false); }
  };

  return (
    <div data-testid="internal-ask">
      <p className="text-sm text-slate-500 mb-4">Ask any compliance question — as an internal reviewer you see all visibility tiers including `internal_only` sources.</p>
      <form onSubmit={submit} className="bg-white border rounded p-4 mb-4 space-y-2">
        <textarea
          required minLength={5}
          value={question}
          onChange={(e) => setQuestion(e.target.value.slice(0, 2000))}
          placeholder="e.g. What is our current pen test posture and when was the last test?"
          rows={3}
          data-testid="internal-question-input"
          className="w-full px-3 py-2 border rounded text-sm"
        />
        <div className="flex justify-end">
          <button type="submit" disabled={asking || question.trim().length < 5} data-testid="internal-ask-submit" className="px-4 py-2 bg-blue-600 text-white text-sm rounded inline-flex items-center gap-1 disabled:opacity-50">
            <Send className="w-4 h-4" /> {asking ? 'Thinking…' : 'Ask'}
          </button>
        </div>
      </form>

      {thread.map((q) => (
        <div key={q.id} data-testid={`internal-answer-${q.id}`} className="bg-white border rounded p-4 mb-3">
          <p className="text-xs text-slate-500 mb-1">{q.created_at?.slice(0, 16).replace('T', ' ')} · conf: {q.confidence}</p>
          <p className="font-medium text-sm mb-2">Q: {q.question_text}</p>
          <div className="text-sm text-slate-700 whitespace-pre-wrap">{q.answer}</div>
          {q.sources?.length > 0 && (
            <div className="mt-2 text-xs text-slate-500">Sources: {q.sources.map((s) => `${s.title} (${s.visibility})`).join(' · ')}</div>
          )}
        </div>
      ))}
    </div>
  );
}
